import type { FC } from "react";
import { Box } from "@mui/material";
import { keyframes } from "@emotion/react";
import { hashKey, type FlyingItem } from "../lib/expandBurst";
import { LIVE_REACTION_EMOJI } from "../lib/reactionEmoji";

const floatUp = keyframes`
  0% { transform: translate(0, 0) scale(0.6); opacity: 0; }
  10% { transform: translate(0, -20px) scale(1); opacity: 1; }
  100% { transform: translate(var(--drift), -72cqh) scale(1.05); opacity: 0; }
`;

interface LiveReactionsLayerProps {
  items: FlyingItem[];
  /** Панель реакций у правого края кадра (десктоп): значки летят левее неё. */
  panelOnRight: boolean;
  phrases: string[];
  onDone: (key: string) => void;
}

/**
 * Слой полёта внутри контейнера плеера, поэтому работает и в полноэкранном
 * режиме. Эмодзи всплывают по правому краю снизу вверх со случайным сдвигом
 * и скоростью, фразы летят облачками медленнее.
 *
 * Слой занимает всю высоту плеера, а высота полёта задана в cqh (доля
 * высоты слоя, container-type: size): при фиксированном полёте в пикселях
 * на небольшом плеере значки упирались в верх слоя и обрезались. Фразы
 * прижаты к правому краю и растут влево, поэтому ширины слоя им хватает.
 */
export const LiveReactionsLayer: FC<LiveReactionsLayerProps> = ({
  items,
  panelOnRight,
  phrases,
  onDone,
}) => (
  <Box
    aria-hidden
    sx={{
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      // Шире колонки панели: самая длинная фраза (24 символа, ~220px) плюс
      // отступ от края должна влезать, иначе режется слева. Не шире плеера.
      width: "min(100%, max(280px, 55%))",
      maxWidth: 480,
      containerType: "size",
      pointerEvents: "none",
      overflow: "hidden",
      zIndex: 205,
    }}
  >
    {items.map((item) => {
      const seed = hashKey(item.key);
      // Правее панели реакций (она у самого края, ~56px), фразы ближе к
      // краю: они растут влево и так не вылезают за левую границу слоя.
      const edge = panelOnRight ? 64 : 12;
      const right =
        item.kind === "phrase" ? edge + (seed % 24) : edge + (seed % 72);
      const drift = ((seed >> 3) % 41) - 20;
      const duration =
        item.kind === "phrase" ? 4200 + (seed % 900) : 2200 + (seed % 900);

      return (
        <Box
          key={item.key}
          onAnimationEnd={() => onDone(item.key)}
          sx={{
            position: "absolute",
            // На мобайле строка панели лежит внизу кадра поперёк: взлёт выше неё.
            bottom: panelOnRight ? 96 : 144,
            right,
            "--drift": `${drift}px`,
            animation: `${floatUp} ${duration}ms ease-out forwards`,
            ...(item.kind === "phrase"
              ? {
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 3,
                  backgroundColor: "rgba(255, 255, 255, 0.92)",
                  color: "#111",
                  fontSize: 14,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }
              : { fontSize: 28, lineHeight: 1 }),
          }}
        >
          {item.kind === "emoji" ? (
            <>
              {LIVE_REACTION_EMOJI[item.type]}
              {item.count > 1 && (
                <Box
                  component="span"
                  sx={{ fontSize: 14, fontWeight: 700, color: "#fff", ml: 0.5 }}
                >
                  ×{item.count}
                </Box>
              )}
            </>
          ) : (
            (phrases[item.index] ?? null)
          )}
        </Box>
      );
    })}
  </Box>
);
